import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { CloudUpload } from '@mui/icons-material';
//
import { ESortDirection, ETableStructureType } from 'ui/EasTable/types';
import {
	getUploadedFilesRequest,
	downloadFileRequest,
} from 'redux/reducers/emProductFileUploads/reducer';
import {
	selectUploadedFiles,
	selectUploadedFilesLoading,
	selectUploadFileLoading,
} from 'redux/reducers/emProductFileUploads/selectors';
import { TEmProductFileUploadsRow } from 'redux/reducers/emProductFileUploads/types';
//
import { TFilters, TOrderBy } from 'redux/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { tableStructure as baseStructure } from './tableStructure';
import { DATES_ARRAY, DATES_ID } from '../../services/constants/dates-constants';
import { PAGE_SIZE } from '../../services/constants/env';
import { getEMerchantRequest } from '../../redux/reducers/monthlyFee/reducer';
import { UploadProductsDialog } from './UploadProductsDialog';

// file input moved to UploadProductsDialog

export const HsCodesRetrieving: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const items = useSelector(selectUploadedFiles);
	const itemsIsLoading = useSelector(selectUploadedFilesLoading);
	const uploading = useSelector(selectUploadFileLoading);

	const tableStructure = useMemo(() => baseStructure, []);

	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector] && item.type === ETableStructureType.DATE) {
			if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
				if (navigateParams[`${String(item.selector)}StartDate`]) {
					acc.push({
						fieldName: item.selector,
						value: navigateParams[`${String(item.selector)}StartDate`] as string,
						operator: '>=',
					});
				}
				if (navigateParams[`${String(item.selector)}EndDate`]) {
					acc.push({
						fieldName: item.selector,
						value: navigateParams[`${String(item.selector)}EndDate`] as string,
						operator: '<=',
					});
				}
				return acc;
			}
			const dates = DATES_ARRAY.find(i => i.id === navigateParams[item.selector]);
			// console.log('dates', dates);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		if (item.type === ETableStructureType.AMOUNT && navigateParams[item.selector]) {
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
			return acc;
		}
		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: navigateParams[item.selector] === 'null' ? 'isNull' : '=',
			});
		return acc;
	}, [] as TFilters);

	if (navigateParams.searchEmId) {
		navigateParamsWithoutPage.push({
			fieldName: 'emId',
			value: navigateParams.searchEmId as string,
			operator: '=',
		});
	}

	const getUploadedFilesRequestPayload = useCallback(() => {
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC
					: ESortDirection.DESC,
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: ESortDirection.DESC,
		} as TOrderBy);

		return {
			orderBy,
			filters: navigateParamsWithoutPage,
			pageSize: PAGE_SIZE,
			pageNumber: navigateParams?.page ? +navigateParams.page : 1,
			searchString: String(navigateParams?.search ?? ''),
		};
	}, [navigateParams, navigateParamsWithoutPage, tableStructure]);

	useEffect(() => {
		dispatch(getUploadedFilesRequest(getUploadedFilesRequestPayload()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	const handleAction = useCallback(
		(id: number, typeOfDialog: string) => {
			const row = items?.data?.find(i => i.id === id);
			if (!row) return;
			if (typeOfDialog === 'Download Uploaded' && row.uploadedFileName) {
				dispatch(
					downloadFileRequest({ id: row.id, fileName: row.uploadedFileName, processed: false }),
				);
			}
			if (typeOfDialog === 'Download Processed' && row.processedFileName) {
				dispatch(
					downloadFileRequest({ id: row.id, fileName: row.processedFileName, processed: true }),
				);
			}
		},
		[dispatch, items],
	);

	const [openUploadDialog, setOpenUploadDialog] = useState(false);
	const handleOpenUploadDialog = () => setOpenUploadDialog(true);
	const handleCloseUploadDialog = () => setOpenUploadDialog(false);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TEmProductFileUploadsRow>
				tableStructure={tableStructure}
				items={items}
				itemsIsLoading={itemsIsLoading}
				handleAction={handleAction}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ml: 1 }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						HS Codes Retrieving
					</Typography>
					<Button
						variant="contained"
						size="small"
						sx={{ textTransform: 'none', height: '24px', p: '4px 10px' }}
						startIcon={<CloudUpload />}
						onClick={handleOpenUploadDialog}
						disabled={uploading}
					>
						Upload products
					</Button>
				</Box>
			</EasTableWithFilter>
			<UploadProductsDialog
				open={openUploadDialog}
				onClose={handleCloseUploadDialog}
				uploadedFilesRequest={getUploadedFilesRequestPayload()}
			/>
		</Box>
	);
};
